import { useState } from 'react';
import { HubHero } from '@/components/features/wellbeing/hub/HubHero';
import { ToolCards } from '@/components/features/wellbeing/hub/ToolCards';
import { useRightNow } from '@/components/features/wellbeing/hub/useRightNow';
import type { MoodId } from '@/components/features/wellbeing/mood/moods';
import { useTodaysMood } from '@/components/features/wellbeing/mood/useTodaysMood';
import { HelplineDialog } from '@/components/features/wellbeing/support/HelplineDialog';
import {
  ThoughtForToday,
  ThoughtSkeleton,
} from '@/components/features/wellbeing/thoughts/ThoughtForToday';
import { useThoughtForToday } from '@/hooks/wellbeing/useThoughtForToday';

/** One quiet line; "Find a helpline" opens the helplines in a dialog. */
function HelplineFooter() {
  const [open, setOpen] = useState(false);
  return (
    <footer className='border-t border-border/60 pt-6'>
      <p className='text-sm text-muted-foreground'>
        Xonera isn&apos;t a substitute for professional help.{' '}
        <button
          type='button'
          onClick={() => setOpen(true)}
          className='tap-target rounded-sm font-medium text-foreground underline underline-offset-4 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
        >
          Find a helpline
        </button>
      </p>
      <HelplineDialog open={open} onClose={() => setOpen(false)} />
    </footer>
  );
}

/**
 * /wellbeing - the hub. "Study well, not just hard."
 *
 * A quiet room, not a dashboard: the usual page header, then a check-in
 * (one question, and ONE next step beside it), the three tools as compact
 * tiles, then - quieter, on the same left edge - the thought for today and
 * a line about where to find real help.
 * The breathing in the Breathe tile is the page's one moment of motion.
 */
export function WellbeingPage() {
  const [mood, setMood] = useTodaysMood();
  const [answeredHere, setAnsweredHere] = useState(false);
  const rightNow = useRightNow(mood, answeredHere);
  const thought = useThoughtForToday(Date.now());

  const onMoodChange = (next: MoodId) => {
    setMood(next);
    setAnsweredHere(true);
  };

  return (
    <div className='h-full overflow-y-auto'>
      {/* On the app's own ground and grid, like every other page. `relative
        isolate` lifts the content above the grid backdrop (a fixed `z-0`
        layer that would otherwise paint over anything unpositioned). */}
      <div className='relative isolate min-h-full'>
        <div className='mx-auto w-full max-w-7xl px-4 pb-16 pt-8 sm:px-6 lg:px-8'>
          <header className='mb-8'>
            <h1 className='mb-2 text-3xl font-bold text-foreground'>
              Wellbeing
            </h1>
            <p className='text-muted-foreground'>Study well, not just hard.</p>
          </header>
          <HubHero
            mood={mood}
            onMoodChange={onMoodChange}
            rightNow={rightNow}
          />

          {/* A clear step from the check-in, so the tools read as their own
            group rather than a continuation of the panel above. */}
          <div className='mt-8'>
            <ToolCards />
          </div>

          {/* Served from the curated catalog. If it cannot be had, the
            section is simply not there - no error on a page meant to be calm. */}
          {(thought.isPending || thought.data) && (
            <section
              aria-labelledby='thought-heading'
              className='mt-10 max-w-2xl'
            >
              <h2
                id='thought-heading'
                className='mb-2 text-sm font-medium text-muted-foreground'
              >
                Thought for today
              </h2>
              {thought.data ? (
                <ThoughtForToday thought={thought.data} quiet />
              ) : (
                <ThoughtSkeleton />
              )}
            </section>
          )}

          <div className='mt-8'>
            <HelplineFooter />
          </div>
        </div>
      </div>
    </div>
  );
}
